import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Scissors, Sparkles, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

/** Original-E-Mail neben dem PDF: Text markieren → als Anfang/Ende übernehmen → zuschneiden, optional pro Absender merken. */
export function EmailOriginalPanel({ companyId, item, onRerendered }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selection, setSelection] = useState("");
  const frameRef = useRef(null);

  useEffect(() => {
    setData(null); setError(null); setSelection("");
    if (!item?.id) return;
    let active = true;
    apiClient.get(`/companies/${companyId}/inbox/${item.id}/email-html`)
      .then((r) => { if (!active) return; setData(r.data); setStart(r.data?.trim?.start_text || ""); setEnd(r.data?.trim?.end_text || ""); })
      .catch((e) => active && setError(e.response?.data?.detail || "Original-Mail konnte nicht geladen werden"));
    return () => { active = false; };
  }, [companyId, item?.id]);

  const readSelection = () => {
    try {
      const s = frameRef.current?.contentWindow?.getSelection()?.toString().replace(/\s+/g, " ").trim();
      if (s) setSelection(s.slice(0, 120));
    } catch { /* cross-origin never happens with srcdoc + allow-same-origin */ }
  };

  const run = async (body) => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/rerender-email`, body);
      toast.success(body.reset ? "Ganze E-Mail wiederhergestellt" : `Rechnungsteil zugeschnitten${r.data.rule_id ? " – für diesen Absender gemerkt" : ""}`);
      setData((d) => (d ? { ...d, trim: r.data.trim || null } : d));
      if (body.reset) { setStart(""); setEnd(""); }
      onRerendered?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zuschnitt fehlgeschlagen");
    } finally { setBusy(false); }
  };

  if (error) return <div className="h-full flex items-center justify-center p-6 text-sm text-rose-700 text-center" data-testid="email-original-error">{error}</div>;
  if (!data) return <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="h-full flex flex-col" data-testid="email-original-panel">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 text-xs text-slate-600 flex items-center justify-between gap-2">
        <span className="truncate">Original-E-Mail{data.source === "imap" ? " (aus Postfach nachgeladen)" : ""} · Text markieren, dann als Anfang/Ende übernehmen</span>
        {data.trim && <span className="rounded-full bg-sky-100 text-sky-800 px-2 py-0.5 whitespace-nowrap" data-testid="email-original-trim-active">Zuschnitt aktiv</span>}
      </div>
      <iframe ref={frameRef} title="Original-E-Mail" srcDoc={data.html} sandbox="allow-same-origin" onMouseUp={readSelection}
        onLoad={() => { try { frameRef.current.contentDocument.addEventListener("mouseup", readSelection); } catch { /* ignore */ } }}
        className="flex-1 w-full bg-white" data-testid="email-original-frame" />
      <div className="border-t border-slate-200 bg-slate-50 p-3 space-y-2">
        {selection && (
          <div className="flex items-center gap-2 text-xs" data-testid="email-original-selection">
            <span className="truncate flex-1 italic text-slate-600">«{selection}»</span>
            <Button variant="outline" size="sm" className="h-7" onClick={() => setStart(selection)} data-testid="email-original-use-start">Als Anfang</Button>
            <Button variant="outline" size="sm" className="h-7" onClick={() => setEnd(selection)} data-testid="email-original-use-end">Als Ende</Button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Input value={start} onChange={(e) => setStart(e.target.value)} placeholder="Beginnt bei … (z.B. Rechnung)" className="h-8 text-xs" data-testid="email-trim-start" />
          <Input value={end} onChange={(e) => setEnd(e.target.value)} placeholder="Endet bei … (optional)" className="h-8 text-xs" data-testid="email-trim-end" />
        </div>
        <div className="flex items-center justify-between gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-sky-600" data-testid="email-trim-remember" />
            Für «{data.sender || "diesen Absender"}» merken
          </label>
          <div className="flex gap-2">
            {data.trim && (
              <Button variant="ghost" size="sm" className="h-8" disabled={busy} onClick={() => run({ reset: true })} data-testid="email-trim-reset">
                <Undo2 className="h-3.5 w-3.5 mr-1" />Ganze Mail
              </Button>
            )}
            <Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700" disabled={busy || !(start.trim() || end.trim())}
              onClick={() => run({ start_text: start.trim(), end_text: end.trim(), remember })} data-testid="email-trim-apply">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Scissors className="h-3.5 w-3.5 mr-1" />}
              Nur Rechnungsteil behalten
            </Button>
          </div>
        </div>
        {remember && <p className="text-[11px] text-slate-500 flex items-center gap-1"><Sparkles className="h-3 w-3" />Nächste Mails dieses Absenders mit gleichem Betreff werden automatisch so zugeschnitten.</p>}
      </div>
    </div>
  );
}
